import { useState } from 'react';
import { Platform } from 'react-native';

import { EncryptionWebView } from '@fhs/cart/src/payment/components/encryption-webview';
import type { CardData } from '@fhs/cart/src/payment/payment-types';
import { usePaymentMethodStore } from '@fhs/cart/src/state/payment-stores';
import {
  ActionSheet,
  Button,
  IconLocked,
  ScrollView,
  XStack,
  YStack,
  createMqStyles,
  tokens,
} from '@fhs/ui';
import CreditCardFormInputs from '@fhs-legacy/frontend/src/components/credit-card-form-inputs';
import { PaymentProcessor } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { CardType, CardTypes, IPaymentMethod } from '@fhs-legacy/frontend/src/state/payment/types';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import { IPaymentErrors, IPaymentState, splitExpiry } from '@fhs-legacy/frontend/src/utils/payment';

import { useApplePayForReload } from '../../hooks/use-apple-pay-for-reload';
import { useGooglePayForReload } from '../../hooks/use-google-pay-for-reload';
import { useVaultNewCard } from '../../hooks/use-vault-new-card';
import {
  FrontendPaymentMethodType,
  useReloadGiftCardMutation,
} from '../../mutations/use-reload-gift-card';
import { formatCentsToDollarString } from '../../utils/helpers';
import { GiftCardInfo } from '../gift-card-info';
import { GiftCardsActionSheetHeader } from '../gift-cards-action-sheet-header';
import { PaymentMethodSelect } from '../payment-method-select';

import { GiftCardReloadAmountSelect, MAX_GIFT_CARD_RELOAD_CENTS } from './gift-card-reload-amount';

type GiftCardsReloadAndPayActionSheetProps = {
  headerTitle: string;
  isVisible: boolean;
  paymentMethods: IPaymentMethod[];
  amountDueInCents: number;
  currentBalanceInCents: number;
  cardLastDigits: string;
  paymentValues: IPaymentState;
  supportedCardTypes: CardType[];
  isDeliveryOrder: boolean;
  savePaymentMethodErrors: IPaymentErrors;
  onClose: () => void;
  onReload: () => void;
  onReloadError?: (error: unknown) => void;
  onCreditCardFormChange: (name: string, value: string) => void;
  onCreditCardFormSubmit: () => boolean;
  isPlacingOrder?: boolean;
  // Required for new card vaulting
  storeId: string;
  processor?: PaymentProcessor;
};

/**
 * Transforms IPaymentState (form values) to CardData (pipeline format)
 */
function transformPaymentValuesToCardData(
  paymentValues: IPaymentState,
  fullName: string
): CardData {
  const { expiryMonth, expiryYear } = splitExpiry(paymentValues.expiry ?? '');

  return {
    fullName: fullName || paymentValues.nameOnCard,
    cardNumber: paymentValues.cardNumber.replace(/\s+/g, ''),
    cvv: paymentValues.cvv ?? '',
    expiryMonth,
    expiryYear,
    postalCode: paymentValues.billingZip,
    vaultCard: paymentValues.saveCard, // Use checkbox value from form
  };
}

export const GiftCardsReloadAndPayActionSheet = ({
  headerTitle,
  isVisible,
  paymentMethods,
  amountDueInCents,
  currentBalanceInCents,
  cardLastDigits,
  paymentValues,
  supportedCardTypes,
  isDeliveryOrder,
  savePaymentMethodErrors,
  onReload,
  onReloadError,
  onCreditCardFormChange,
  onCreditCardFormSubmit,
  onClose = () => {},
  isPlacingOrder = false,
  storeId,
  processor,
}: GiftCardsReloadAndPayActionSheetProps) => {
  const [selectedReloadAmount, setSelectedReloadAmount] = useState(amountDueInCents.toString());
  const [otherReloadAmount, setOtherReloadAmount] = useState('0'); // Used for the "other" reload amount input

  const mqStyles = useMqStyles();
  const { mutate: fireReloadGiftCardMutation, isPending: isPendingReloadGiftCard } =
    useReloadGiftCardMutation({
      onReloadSuccess: onReload,
      onReloadError: onReloadError,
    });

  const { vaultNewCard, isVaulting } = useVaultNewCard({
    storeId,
    processor,
  });

  const { requestGooglePayForReload, isLoading: isGooglePayLoading } = useGooglePayForReload({
    processor: processor ?? PaymentProcessor.ORBITAL,
    storeId,
  });

  const { requestApplePayForReload, isLoading: isApplePayLoading } = useApplePayForReload({
    processor: processor ?? PaymentProcessor.ORBITAL,
  });

  const paymentMethodsToSelect = paymentMethods
    .filter(method => !method?.giftCard) // ignore gift cards
    .filter(
      (method, index, self) =>
        // removes duplicated payment methods (selected method appears twice)
        index === self.findIndex(option => option.accountIdentifier === method.accountIdentifier)
    );

  const [selectedPaymentOption, setSelectedPaymentOption] = useState(
    paymentMethodsToSelect[0]?.accountIdentifier ?? ''
  );

  const showNewCreditCardForm = selectedPaymentOption === FrontendPaymentMethodType.NEW_CARD;

  const reloadAmount =
    selectedReloadAmount === 'other' ? Number(otherReloadAmount) : Number(selectedReloadAmount);

  const formattedSelectedReloadAmount = formatCentsToDollarString(reloadAmount);

  const handleReloadGiftCard = async () => {
    let paymentMethodToUse = selectedPaymentOption;

    // If user selected "newCard", handle vaulting based on saveCard checkbox
    if (showNewCreditCardForm) {
      const isCreditCardValid = onCreditCardFormSubmit();

      if (!isCreditCardValid) {
        return;
      }

      const cardData = transformPaymentValuesToCardData(paymentValues, paymentValues.nameOnCard);

      // Set credit type in store (required by VaultService)
      usePaymentMethodStore.getState().setCreditType(cardData.cardNumber);

      // Only vault if user checked "Save card" checkbox
      if (paymentValues.saveCard) {
        const vaultResult = await vaultNewCard(cardData);

        if (!vaultResult) {
          // Vaulting failed - error is handled by the hook
          return;
        }

        // Use the newly vaulted card's accountIdentifier
        paymentMethodToUse = vaultResult.accountIdentifier;
      } else {
        // One-time payment: Don't vault, use empty ID
        // The encrypted card data will be sent via the mutation hook
        paymentMethodToUse = '';
      }
    }

    let paymentMethodType: FrontendPaymentMethodType = FrontendPaymentMethodType.SAVED_CARD;
    if (selectedPaymentOption === FrontendPaymentMethodType.NEW_CARD) {
      paymentMethodType = FrontendPaymentMethodType.NEW_CARD;
    } else if (selectedPaymentOption === CardTypes.APPLE_PAY) {
      paymentMethodType = FrontendPaymentMethodType.APPLE_PAY;
    } else if (selectedPaymentOption === CardTypes.GOOGLE_PAY) {
      paymentMethodType = FrontendPaymentMethodType.GOOGLE_PAY;
    }

    // Handle Google Pay - need to trigger payment sheet first
    if (paymentMethodType === FrontendPaymentMethodType.GOOGLE_PAY) {
      logger.info({
        message: '[GiftCardReload] Triggering Google Pay for reload',
        amount: reloadAmount,
      });

      let googlePayDetails: Awaited<ReturnType<typeof requestGooglePayForReload>>;
      try {
        googlePayDetails = await requestGooglePayForReload(reloadAmount);
      } catch (err) {
        logger.error({
          message: '[GiftCardReload] Google Pay failed',
          error: err instanceof Error ? err.message : String(err),
        });
        onReloadError?.(err);
        return;
      }

      if (!googlePayDetails) {
        // User cancelled or payment failed - don't proceed
        logger.warn({
          message: '[GiftCardReload] Google Pay cancelled by user',
        });
        return;
      }

      // Fire mutation with Google Pay token data
      const reloadGiftCardPayload = {
        amount: reloadAmount,
        paymentMethodId: paymentMethodToUse,
        paymentMethodType,
        googlePayDetails,
      };

      fireReloadGiftCardMutation(reloadGiftCardPayload);
      return;
    }

    // Handle Apple Pay - need to trigger payment sheet first
    if (paymentMethodType === FrontendPaymentMethodType.APPLE_PAY) {
      logger.info({
        message: '[GiftCardReload] Triggering Apple Pay for reload',
        amount: reloadAmount,
      });

      let applePayDetails: Awaited<ReturnType<typeof requestApplePayForReload>>;
      try {
        applePayDetails = await requestApplePayForReload(reloadAmount);
      } catch (err) {
        logger.error({
          message: '[GiftCardReload] Apple Pay failed',
          error: err instanceof Error ? err.message : String(err),
        });
        onReloadError?.(err);
        return;
      }

      if (!applePayDetails) {
        // User cancelled or payment failed - don't proceed
        logger.warn({
          message: '[GiftCardReload] Apple Pay cancelled by user',
        });
        return;
      }

      // Fire mutation with Apple Pay token data
      const reloadGiftCardPayload = {
        amount: reloadAmount,
        paymentMethodId: paymentMethodToUse,
        paymentMethodType,
        applePayDetails,
      };

      fireReloadGiftCardMutation(reloadGiftCardPayload);
      return;
    }

    // For other payment methods (saved cards, new cards), fire mutation normally
    const reloadGiftCardPayload = {
      amount: reloadAmount,
      paymentMethodId: paymentMethodToUse,
      paymentMethodType,
      storePaymentMethod: showNewCreditCardForm ? paymentValues.saveCard : undefined,
      // Pass card data for encryption if one-time payment (not vaulted)
      ...(showNewCreditCardForm &&
        !paymentValues.saveCard && {
          cardData: transformPaymentValuesToCardData(paymentValues, paymentValues.nameOnCard),
          processor,
          storeId,
        }),
    };

    fireReloadGiftCardMutation(reloadGiftCardPayload);
  };

  const isLoading =
    isPlacingOrder ||
    isPendingReloadGiftCard ||
    isVaulting ||
    isGooglePayLoading ||
    isApplePayLoading;

  // Disable button if "Other" is selected and the amount is less than the minimum required
  const isReloadAmountInsufficient =
    selectedReloadAmount === 'other' && reloadAmount < amountDueInCents;

  // Disable button if "Other" is selected and the amount exceeds the maximum allowed ($200)
  const isReloadAmountExcessive =
    selectedReloadAmount === 'other' && reloadAmount > MAX_GIFT_CARD_RELOAD_CENTS;

  const isButtonDisabled = isLoading || isReloadAmountInsufficient || isReloadAmountExcessive;

  return (
    <ActionSheet
      onRequestClose={onClose}
      onClose={onClose}
      isVisible={isVisible}
      grabber={false}
      noPanGestureDetection
      containerStyle={{ height: '90%' }}
      drawerProps={{ style: { flex: 1, backgroundColor: tokens.colors.$white, borderRadius: 12 } }} // Needed to make the flat list scroll inside the actionsheet
    >
      <GiftCardsActionSheetHeader
        headerTitle={headerTitle}
        onClose={onClose}
        onPressPrev={onClose}
        disableBackButton={false}
      />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={mqStyles.contentWrapper}>
        <GiftCardInfo
          lastCardDigits={cardLastDigits}
          balance={formatCentsToDollarString(currentBalanceInCents)}
        />
        <YStack style={{ marginTop: 16 }}>
          <PaymentMethodSelect
            paymentMethods={paymentMethodsToSelect}
            selectedOption={selectedPaymentOption}
            onSelectOption={setSelectedPaymentOption}
            customHeader={
              <GiftCardReloadAmountSelect
                amountDueInCents={Number(amountDueInCents)}
                selectedReloadAmount={selectedReloadAmount}
                onSelectReloadAmount={setSelectedReloadAmount}
                otherReloadAmount={otherReloadAmount}
                onChangeOtherReloadAmount={setOtherReloadAmount}
              />
            }
          />
          {showNewCreditCardForm && (
            <>
              <CreditCardFormInputs
                ariaLabel="credit-card-form-inputs"
                isDelivery={isDeliveryOrder}
                onChange={onCreditCardFormChange}
                paymentValues={paymentValues}
                errors={savePaymentMethodErrors}
                withBorder={false}
                withPadding={false}
                supportedCardTypes={supportedCardTypes}
              />
              {Platform.OS !== 'web' && (
                <EncryptionWebView
                  cardData={transformPaymentValuesToCardData(
                    paymentValues,
                    paymentValues.nameOnCard
                  )}
                />
              )}
            </>
          )}
        </YStack>
      </ScrollView>
      <XStack style={mqStyles.footer}>
        <Button
          size="xl"
          style={mqStyles.buttonContainer}
          onPress={handleReloadGiftCard}
          loading={isLoading}
          disabled={isButtonDisabled}
        >
          <XStack style={mqStyles.buttonLeftWrapper}>
            <Button.Icon size={20}>
              <IconLocked />
            </Button.Icon>
            <Button.Text>Reload & Order</Button.Text>
          </XStack>
          <Button.Text>${formattedSelectedReloadAmount}</Button.Text>
        </Button>
      </XStack>
    </ActionSheet>
  );
};

const useMqStyles = createMqStyles({
  contentWrapper: {
    $base: {
      padding: 16,
    },
    $gteDesktopLg: {
      paddingHorizontal: 28,
    },
  },
  buttonContainer: {
    $base: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 28,
    },
  },
  buttonLeftWrapper: {
    $base: {
      alignItems: 'center',
    },
  },
  footer: {
    $base: {
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 16,
      borderTopWidth: 1,
      borderColor: tokens.colors.$black10,
      backgroundColor: tokens.colors.$white,
      borderBottomRightRadius: 12,
      borderBottomLeftRadius: 12,
    },
  },
});
